import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { StoryViewerComponent } from '../story-viewer/story-viewer.component';
import { PublicationClass } from 'src/app/Models/Classes/Publications/publication-class';
import { NewPublicationButtonComponent } from '../../common-elements/buttons/new-publication-button/new-publication-button.component';
import { PublicationService } from 'src/app/services/publications/publication.service';
import { Subscription } from 'rxjs';

interface StoryAuthorGroup {
  key: string;
  authorId: string | null;
  profileId: string | null;
  username: string;
  profilePhoto: string;
  stories: Array<PublicationClass>;
  hasActiveStories: boolean;
  hasUnseenStories: boolean;
  allStoriesSeen: boolean;
}

interface StoryAuthorInfo {
  authorId: string | null;
  profileId: string | null;
  username: string;
  profilePhoto: string;
}

@Component({
  selector: 'app-stories',
  templateUrl: './stories.component.html',
  styleUrls: ['./stories.component.scss'],
})
export class StoriesComponent implements OnInit, OnChanges, OnDestroy {
  @Input() stories: Array<PublicationClass> = [];
  @Input() profile: any;

  public storyAuthors: Array<StoryAuthorGroup> = [];
  public currentUserStoryGroup: StoryAuthorGroup | undefined;
  public currentUserHasStories = false;
  public currentUserHasUnseenStories = false;
  public currentUserAllStoriesSeen = false;
  public currentUserProfilePhoto = 'assets/icon/usuario.png';
  private storiesSubscription?: Subscription;

  constructor(
    private modalCtrl: ModalController,
    private newpub: NewPublicationButtonComponent,
    private publicationService: PublicationService,
  ) { }

  ngOnInit(): void {
    this.rebuildStoryGroups();
    this.storiesSubscription = this.publicationService.storiesChanged$.subscribe(stories => {
      this.stories = Array.isArray(stories) ? stories : [];
      this.rebuildStoryGroups();
    });
    this.publicationService.loadStories().catch(() => {
      // Keep the add-story action available if the shared Stories request fails.
    });
  }

  ngOnDestroy(): void {
    this.storiesSubscription?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stories'] || changes['profile']) {
      this.rebuildStoryGroups();
    }

    if (changes['profile']?.currentValue) {
      this.publicationService.loadStories(true).catch(() => {
        // Keep the add-story action available if the optional refresh fails.
      });
    }
  }

  public msToDatetime(ms: number): string {
    const date = new Date(ms * 1000);
    return date.toLocaleString();
  }

  public openCurrentUserStoriesOrCreate(event?: Event): void {
    this.stopEvent(event);

    if (this.currentUserStoryGroup?.stories.length) {
      this.openStoryViewer(this.currentUserStoryGroup.stories);
      return;
    }

    this.newStory();
  }

  public openAuthorStories(author: StoryAuthorGroup, event?: Event): void {
    this.stopEvent(event);

    if (author?.stories?.length) {
      this.openStoryViewer(author.stories);
    }
  }

  public openStoryViewer(
    storiesOrIndex: Array<PublicationClass> | number,
    tapped = 0,
  ): void {
    const stories = typeof storiesOrIndex === 'number' ? this.stories : storiesOrIndex;
    const requestedIndex = typeof storiesOrIndex === 'number' ? storiesOrIndex : tapped;
    const authorStories = Array.isArray(stories) ? stories.filter(Boolean) : [];
    if (!authorStories.length) {
      return;
    }

    const safeRequestedIndex = Number.isFinite(requestedIndex) ? requestedIndex : 0;
    const initialStory = Math.max(0, Math.min(safeRequestedIndex, authorStories.length - 1));
    this.modalCtrl.create({
      component: StoryViewerComponent,
      cssClass: 'detailsInModal',
      canDismiss: true,
      componentProps: {
        stories: authorStories,
        tapped: initialStory,
      }
    }).then(modal => modal.present()).catch(error => {
      console.error('Error opening story viewer:', error);
    });
  }

  /**
   * Kept as a public method for callers that used the previous Stories API.
   * The current-user action now uses the already grouped story collection.
   */
  public searchMyStorie(stories?: Array<PublicationClass>): void {
    if (!Array.isArray(stories)) {
      this.openCurrentUserStoriesOrCreate();
      return;
    }

    const currentUserId = this.extractUserId(this.profile);
    const currentProfileId = this.extractProfileId(this.profile);
    const currentProfileUsername = this.normalizedUsername(this.extractUsername(this.profile));
    const currentUserStories = stories.filter(story => {
      const author = this.extractStoryAuthor(story);
      const authorGroup = {
        authorId: author.authorId,
        profileId: author.profileId,
        username: author.username,
      } as StoryAuthorGroup;
      return this.isCurrentUser(authorGroup, currentUserId, currentProfileId, currentProfileUsername);
    });

    if (currentUserStories.length) {
      this.openStoryViewer(currentUserStories);
    } else {
      this.newStory();
    }
  }

  public newStory(event?: Event): void {
    this.stopEvent(event);
    this.newpub.presentModalHistory();
  }

  public trackByAuthor(_index: number, author: StoryAuthorGroup): string {
    return author.key;
  }

  public handleImageError(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (image && image.getAttribute('src') !== 'assets/icon/usuario.png') {
      image.src = 'assets/icon/usuario.png';
    }
  }

  private rebuildStoryGroups(): void {
    const groupedAuthors = new Map<string, StoryAuthorGroup>();
    const availableStories = Array.isArray(this.stories) ? this.stories : [];

    availableStories.forEach((story, index) => {
      if (!story) {
        return;
      }

      const author = this.extractStoryAuthor(story);
      let authorGroup = this.findAuthorGroup(groupedAuthors, author);

      if (!authorGroup) {
        const key = this.createAuthorKey(author, index);
        authorGroup = {
          key,
          authorId: author.authorId,
          profileId: author.profileId,
          username: author.username,
          profilePhoto: author.profilePhoto,
          stories: [],
          hasActiveStories: false,
          hasUnseenStories: false,
          allStoriesSeen: false,
        };
        groupedAuthors.set(key, authorGroup);
      } else {
        // If an earlier record only had a username, upgrade its key when a
        // later record provides the canonical author ID.
        if ((!authorGroup.authorId && author.authorId) || (!authorGroup.profileId && author.profileId)) {
          groupedAuthors.delete(authorGroup.key);
          authorGroup.authorId = author.authorId;
          authorGroup.profileId = author.profileId;
          authorGroup.key = this.createAuthorKey(author, index);
          groupedAuthors.set(authorGroup.key, authorGroup);
        }

        // A later record may have better profile data than the first one.
        if (author.username !== 'Usuario') {
          authorGroup.username = author.username;
        }
        if (author.profilePhoto !== 'assets/icon/usuario.png') {
          authorGroup.profilePhoto = author.profilePhoto;
        }
      }

      authorGroup.stories.push(story);
      authorGroup.hasActiveStories = authorGroup.stories.length > 0;
    });

    const allAuthors = Array.from(groupedAuthors.values());
    const currentUserId = this.extractUserId(this.profile);
    const currentProfileId = this.extractProfileId(this.profile);
    const currentProfileUsername = this.normalizedUsername(this.extractUsername(this.profile));

    allAuthors.forEach(author => {
      author.hasActiveStories = author.stories.length > 0;
      author.hasUnseenStories = this.publicationService.hasUnseenStories(author.stories, this.profile);
      author.allStoriesSeen = author.hasActiveStories && !author.hasUnseenStories;
    });

    this.currentUserStoryGroup = allAuthors.find(author =>
      this.isCurrentUser(author, currentUserId, currentProfileId, currentProfileUsername)
    );
    this.currentUserHasStories = !!this.currentUserStoryGroup?.stories.length;
    this.currentUserHasUnseenStories = !!this.currentUserStoryGroup?.hasUnseenStories;
    this.currentUserAllStoriesSeen = !!this.currentUserStoryGroup?.allStoriesSeen;
    this.currentUserProfilePhoto = this.resolveProfilePhoto(this.profile);
    this.storyAuthors = allAuthors.filter(author => author !== this.currentUserStoryGroup);
  }

  private findAuthorGroup(
    groupedAuthors: Map<string, StoryAuthorGroup>,
    author: StoryAuthorInfo,
  ): StoryAuthorGroup | undefined {
    if (author.authorId) {
      const byId = groupedAuthors.get(`id:${author.authorId}`);
      if (byId) {
        return byId;
      }
    }

    if (author.profileId) {
      const byProfileId = groupedAuthors.get(`profile:${author.profileId}`);
      if (byProfileId) {
        return byProfileId;
      }
    }

    const username = this.normalizedUsername(author.username);
    if (!username || author.username === 'Usuario') {
      return undefined;
    }

    const byUsername = groupedAuthors.get(`username:${username}`);
    if (byUsername) {
      return byUsername;
    }

    return Array.from(groupedAuthors.values()).find(group =>
      this.normalizedUsername(group.username) === username
    );
  }

  private extractStoryAuthor(story: any): StoryAuthorInfo {
    const userProfile = this.firstValue(
      story?.userProfile,
      story?.user_profile,
      story?.author,
      story?.user,
    ) || {};

    const explicitAuthorId = this.firstValue(
      story?.user_id,
      story?.userId,
      story?.author_id,
      story?.authorId,
      userProfile?.user_id,
      userProfile?.userId,
      story?.__storyAuthor?.user_id,
      story?.__storyAuthor?.userId,
    );
    const authorId = this.normalizeId(explicitAuthorId || this.firstValue(
      story?.user?.user_id,
      story?.user?.userId,
      story?.user?.id,
      story?.author?.user_id,
      story?.author?.userId,
      story?.author?.id,
    ));
    const explicitProfileId = this.firstValue(
      story?.profile_id,
      story?.profileId,
      userProfile?.profile_id,
      userProfile?.profileId,
      story?.user?.profile_id,
      story?.user?.profileId,
      story?.author?.profile_id,
      story?.author?.profileId,
      story?.__storyAuthor?.profile_id,
      story?.__storyAuthor?.profileId,
    );
    const profileId = this.normalizeId(explicitProfileId || this.firstValue(
      story?.profile?.id,
      story?.profile?.profile_id,
      story?.user?.profile_id,
      story?.user?.profileId,
      story?.author?.profile_id,
      story?.author?.profileId,
    ));

    // The Story row's top-level handle is authoritative. Nested users may come
    // from an embedded publication/comment and must not rename the Story owner.
    const username = this.extractUsername(story, userProfile) || 'Usuario';
    const profilePhoto = this.resolveProfilePhoto(this.firstValue(
      userProfile?.profile_photo,
      userProfile?.profilePhoto,
      userProfile?.photo,
      userProfile?.avatar,
      userProfile?.image,
      story?.profile_photo,
      story?.profilePhoto,
      story?.user_photo,
      story?.userPhoto,
      story?.author?.profile_photo,
      story?.user?.profile_photo,
    ));

    return { authorId, profileId, username, profilePhoto };
  }

  private extractUsername(...sources: any[]): string {
    const username = this.firstValue(
      ...sources.reduce((values: any[], source: any) => values.concat([
        source?.username,
        source?.userName,
        source?.user?.username,
        source?.user?.userName,
        source?.author?.username,
        source?.author?.userName,
      ]), [])
    );

    return typeof username === 'string' ? username.trim() : '';
  }

  private extractProfileId(profile: any): string | null {
    return this.normalizeId(this.firstValue(
      profile?.profile_id,
      profile?.profileId,
      profile?.id,
      profile?.profile?.id,
    ));
  }

  private extractUserId(profile: any): string | null {
    return this.normalizeId(this.firstValue(
      profile?.user_id,
      profile?.userId,
      profile?.user?.user_id,
      profile?.user?.userId,
      profile?.user?.id,
    ));
  }

  private createAuthorKey(author: StoryAuthorInfo, index: number): string {
    if (author.authorId) {
      return `id:${author.authorId}`;
    }

    if (author.profileId) {
      return `profile:${author.profileId}`;
    }

    const username = this.normalizedUsername(author.username);
    return username ? `username:${username}` : `unknown:${index}`;
  }

  private isCurrentUser(
    author: StoryAuthorGroup,
    currentUserId: string | null,
    currentProfileId: string | null,
    currentProfileUsername: string,
  ): boolean {
    const authorUsername = this.normalizedUsername(author.username);

    // A canonical username is stronger than a legacy numeric identifier here.
    // Some old Story/feed payloads reused a nested user's ID; allowing that ID
    // to classify the Story as the viewer would hide another author's Story
    // from the shared bar.
    if (currentProfileUsername && authorUsername && currentProfileUsername !== authorUsername) {
      return false;
    }

    if (currentUserId && author.authorId && currentUserId === author.authorId) {
      return true;
    }

    if (currentProfileId && author.profileId && currentProfileId === author.profileId) {
      return true;
    }

    if (author.username === 'Usuario') {
      return false;
    }

    return !!currentProfileUsername && currentProfileUsername === authorUsername;
  }

  private resolveProfilePhoto(source: any): string {
    let photo = source;

    for (let depth = 0; depth < 3 && photo && typeof photo === 'object'; depth++) {
      const nestedPhoto = this.firstValue(
        photo?.url,
        photo?.route,
        photo?.path,
        photo?.src,
        photo?.localUrl,
        photo?.photo_url,
        photo?.photoUrl,
        photo?.profile_photo,
        photo?.profilePhoto,
      );
      if (nestedPhoto === photo) {
        break;
      }
      photo = nestedPhoto;
    }

    const value = typeof photo === 'string' ? photo.trim() : '';
    if (!value || value === 'null' || value === 'undefined') {
      return 'assets/icon/usuario.png';
    }

    if (/^(https?:|blob:|data:|capacitor:|file:)/i.test(value) || value.startsWith('assets/')) {
      return value;
    }

    return 'https://laniakea-multimedia.s3.amazonaws.com/' + value.replace(/^\/+/, '');
  }

  private normalizeId(value: any): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();
    return normalized ? normalized : null;
  }

  private normalizedUsername(value: string): string {
    return (value || '').trim().replace(/^@+/, '').toLocaleLowerCase();
  }

  private firstValue(...values: any[]): any {
    return values.find(value =>
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && !value.trim())
    );
  }

  private stopEvent(event?: Event): void {
    event?.stopPropagation();
  }
}
